import { useCallback, useState } from 'react';
import { userAdminService } from '@services';
import { showToast } from '@utils/toast';
import { getApiErrorMessage } from '@utils/apiError';
import type { AdminUser } from '@app-types/admin';
import type { ContactUserStatusChange, UseContactUserStatusChangeOptions } from '@app-types/contactUserStatusChange';

/**
 * The confirm-then-save flow of switching a Contact-User between Active and Inactive,
 * shared by the deactivate and activate hooks so both save and report the same way.
 */
export const useContactUserStatusChange = ({
  user,
  isActive,
  successMessage,
  failureMessage,
  onChanged
}: UseContactUserStatusChangeOptions): ContactUserStatusChange => {
  const [target, setTarget] = useState<AdminUser | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const subject = target ?? user;

  // Stable, so a table cell renderer can depend on them without being rebuilt on every render.
  const openConfirm = useCallback((other?: AdminUser) => {
    setTarget(other ?? null);
    setIsConfirmOpen(true);
  }, []);
  const closeConfirm = useCallback(() => setIsConfirmOpen(false), []);

  const confirm = async () => {
    if (!subject) return;
    setIsSubmitting(true);
    try {
      const result = await userAdminService.updateUser(subject.id, { is_active: isActive });
      setIsConfirmOpen(false);
      onChanged();
      showToast.success(typeof successMessage === 'function' ? successMessage(result) : successMessage);
    } catch (error) {
      console.error('Status change failed', error);
      showToast.error(getApiErrorMessage(error, failureMessage));
    } finally {
      setIsSubmitting(false);
    }
  };

  return { subject, isConfirmOpen, isSubmitting, openConfirm, closeConfirm, confirm };
};
